import { useState } from 'react';
import { faqData } from '@/mocks/homeData';

export default function FAQSection() {
  const [openIds, setOpenIds] = useState<number[]>([1]);

  const toggleFAQ = (id: number) => {
    setOpenIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  return (
    <section className="relative py-14 md:py-20 px-4 md:px-6 bg-background-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10 md:mb-14">
          <div className="flex items-center justify-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full bg-accent-500"></span>
            <span className="text-xs font-semibold text-foreground-400 uppercase tracking-widest">FAQ</span>
          </div>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight">
            Common Questions
            <span className="block text-accent-600 font-light italic">From Buyers</span>
          </h2>
          <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
            Everything you need to know about the <strong>AllChinaBuy Spreadsheet</strong> — answered by our team.
          </p>
        </div>

        {/* FAQ Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
          {faqData.map((faq) => {
            const isOpen = openIds.includes(faq.id);
            return (
              <div
                key={faq.id}
                className={`rounded-xl border transition-all duration-200 cursor-pointer ${
                  isOpen
                    ? 'bg-accent-50 border-accent-200'
                    : 'bg-background-100 border-background-200/60 hover:border-background-300'
                }`}
                onClick={() => toggleFAQ(faq.id)}
              >
                <div className="p-4 md:p-5">
                  {/* Header */}
                  <div className="flex items-start gap-3">
                    <span
                      className={`w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5 transition-colors ${
                        isOpen ? 'bg-accent-500 text-background-50' : 'bg-background-200 text-foreground-500'
                      }`}
                    >
                      <i className={`text-xs ${isOpen ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
                    </span>
                    <div className="flex-1 min-w-0">
                      <h3 className={`text-sm md:text-base font-semibold transition-colors ${
                        isOpen ? 'text-accent-700' : 'text-foreground-950'
                      }`}>
                        {faq.question}
                      </h3>
                    </div>
                  </div>

                  {/* Answer */}
                  <div
                    className={`grid transition-all duration-300 ease-in-out ${
                      isOpen ? 'grid-rows-[1fr] mt-3' : 'grid-rows-[0fr]'
                    }`}
                  >
                    <div className="overflow-hidden">
                      <p className="text-xs md:text-sm text-foreground-600 leading-relaxed pl-9">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className={`px-5 pb-4 flex items-center gap-2 transition-opacity ${isOpen ? 'opacity-100' : 'opacity-0'}`}>
                  <span className="text-[10px] font-semibold text-accent-600 bg-accent-100 px-2 py-0.5 rounded-full">
                    {faq.category}
                  </span>
                  <span className="text-[10px] text-foreground-400">
                    Updated: {faq.updated}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Schema.org FAQ markup */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: faqData.map((faq) => ({
                '@type': 'Question',
                name: faq.question,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: faq.answer,
                },
              })),
            }),
          }}
        />
      </div>
    </section>
  );
}